<template>
  <div v-if="view === 'root'" class="dialog-step">
    <div class="dialog-step__content">
      <p class="dialog-step__description">
        {{ $t('dialogs.requestTokensTitle') }}
      </p>
      <PMessage :closable="false" severity="info">
        {{ $t('dialogs.requestTokensDescription', [mainBalance.symbol.value]) }}
      </PMessage>
      <PInlineMessage v-if="!haveBalance" class="p-error my-2">
        {{ $t('pools.insufficientBalance', [mainBalance.symbol.value]) }}
      </PInlineMessage>
      <div v-tooltip.bottom="tooltipMsgBTC">
        <PButton
          :disabled="requestDisabledBTC"
          :loading="BTCRestriction.loading.value"
          class="send-button p-button-primary"
          @click="requestToken(tokenAddress('BTC'))"
        >
          <div
            class="w-full relative flex align-items-center justify-content-center"
          >
            <span class="font-medium">
              {{ $t('dialogs.requestToken', ['BTC']) }}
            </span>
            <span
              v-if="!BTCRestriction.status.value.status"
              class="text-sm font-bold absolute right-0"
              >{{ duration(BTCRestriction.cooldown.value) }}</span
            >
          </div>
        </PButton>
      </div>
      <div v-tooltip.bottom="tooltipMsgUSDT">
        <PButton
          :disabled="requestDisabledUSDT"
          :loading="USDTRestriction.loading.value"
          class="send-button p-button-primary"
          @click="requestToken(tokenAddress('USDT'))"
        >
          <div
            class="w-full relative flex align-items-center justify-content-center"
          >
            <span class="font-medium">
              {{ $t('dialogs.requestToken', ['USDT']) }}
            </span>
            <span
              v-if="!USDTRestriction.status.value.status"
              class="text-sm font-bold absolute right-0"
              >{{ duration(USDTRestriction.cooldown.value) }}</span
            >
          </div>
        </PButton>
      </div>
    </div>
  </div>
  <SendTransaction
    v-else
    :payload="payload"
    title="Request Tokens"
    @back="onBack"
    @close="onClose"
    @reject="onRejected"
    @success="onSuccess"
  >
  </SendTransaction>
</template>

<script lang="ts" setup>
import { SendTransaction } from '@/components/Dialogs/Steps';
import { useCurrentNativeBalance } from '@/composables/useAccountBalance';
import { useFaucetTokenRestriction } from '@/composables/useFaucetTokenRestriction';
import { ChainsEnum, CORRECT_CHAIN } from '@/constants';
import { useDialogStore } from '@/store';
import { TxPayloadCallFunction } from '@/types/aptosResources';
import { duration } from '@/utils';
import { composeType, extractAddressFromType } from '@/utils/contracts';
import { moduleAddress, tokenAddress } from '@/utils/networkData';
import { useWalletPlugin } from '@/composables/useWalletPlugin';
import { computed, inject, ref, watchEffect } from 'vue';
import { useI18n } from '@/composables/useI18n';

type IProps = {
  visible?: boolean;
};

defineProps<IProps>();
defineEmits(['update:visible']);

const { t } = useI18n();
const { account } = useWalletPlugin();
const mainBalance = useCurrentNativeBalance();

const dialogStore = useDialogStore();
const dialogRef = inject('dialogRef');

const view = ref<'root' | 'tx'>('root');
const selectedToken = ref<string>();

const payload = computed<TxPayloadCallFunction>(() => {
  const faucetModuleName = CORRECT_CHAIN.id === ChainsEnum['lumio-testnet'] ? 'FaucetL2' : 'Faucet';
  const faucetModule = moduleAddress(faucetModuleName);
  return {
    type: 'entry_function_payload',
    function: composeType(faucetModule, 'request'),
    typeArguments: [selectedToken.value || ''],
    functionArguments:
      CORRECT_CHAIN.id === ChainsEnum['lumio-testnet']
        ? ['0x45a6dce5d868431d7f0c224a0122e64372eeb1697309529c9aaa94a97e9d331e']
        : [extractAddressFromType(faucetModule)],
  };
});

const BTCRestriction = useFaucetTokenRestriction(
  computed(() => account.value?.address as string),
  tokenAddress('BTC'),
);
const USDTRestriction = useFaucetTokenRestriction(
  computed(() => account.value?.address as string),
  tokenAddress('USDT'),
);

const haveBalance = computed(() => {
  return mainBalance.balance.value > 500;
});

const requestDisabledBTC = computed(() => {
  return (
    BTCRestriction.loading.value ||
    !BTCRestriction.status.value.status ||
    !haveBalance.value
  );
});

const tooltipMsgBTC = computed(() => {
  return !BTCRestriction.status.value.status &&
    BTCRestriction.cooldown.value > 1000
    ? t('dialogs.youHaveSentTokenRequest', ['BTC'])
    : '';
});

const requestDisabledUSDT = computed(() => {
  return (
    USDTRestriction.loading.value ||
    !USDTRestriction.status.value.status ||
    !haveBalance.value
  );
});

const tooltipMsgUSDT = computed(() => {
  return !USDTRestriction.status.value.status &&
    USDTRestriction.cooldown.value > 1000
    ? t('dialogs.youHaveSentTokenRequest', ['USDT'])
    : '';
});

function requestToken(token: string) {
  selectedToken.value = token;
  view.value = 'tx';
  const currentDialogTitle = dialogStore.dialogTitle;
  const currentHasBack = dialogStore.hasBack;
  dialogStore.constructedBack(() => {
    view.value = 'root';
    dialogStore.setDialogTitle(currentDialogTitle);
    dialogStore.setHasBack(currentHasBack);
  });
  dialogStore.setDialogTitle(t('dialogs.requestTokens'));
  dialogStore.setHasBack(true);
}

function refetchRestrictions() {
  BTCRestriction.refetch();
  USDTRestriction.refetch();
}

function onBack() {
  view.value = 'root';
}

function onClose() {
  dialogRef.value.close();
}

function onRejected() {
  view.value = 'root';
  refetchRestrictions();
}

function onSuccess() {
  view.value = 'root';
  refetchRestrictions();
}

watchEffect((value) => {
  if (!value) {
    view.value = 'root';
  } else {
    refetchRestrictions();
  }
});
</script>

<style lang="scss" scoped>
.send-button {
  width: 100%;
  margin-top: 10px;
}

.step {
  ::v-deep(.p-blockui) {
    opacity: 0;
  }
}

.dialog-step__description {
  margin: 0px 0px 30px;
}
</style>
